import { useState } from 'react';
import { toast } from 'sonner';
import { useClient } from 'hooks/useClient';
import { readApiError } from './api-error';
import { saveCsv } from './download-csv';

/** `YYYY-MM` for the current month, in the viewer's time zone (the server parses it as a UTC month). */
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * The sessions behind the plan for a month, per account and website, as a CSV
 * from `GET /api/billing/usage/report`, fetched authenticated and saved as a
 * download (see download-csv.ts).
 *
 * A client org under an agency gets a 403: the report is the agency's document.
 * The settings screen does not offer it there.
 */
export const useUsageReport = () => {
  const apiClient = useClient();
  const [isDownloading, setIsDownloading] = useState(false);
  const [month, setMonth] = useState(currentMonth);

  const download = async () => {
    setIsDownloading(true);
    try {
      const csv = await apiClient.getText(`/billing/usage/report?month=${month}&format=csv`);
      saveCsv({ csv, filename: `usage-${month}.csv` });
    } catch (error) {
      const { message } = readApiError(error);
      toast.error('Could not download the report', { description: message ?? 'Please try again in a moment.' });
    } finally {
      setIsDownloading(false);
    }
  };

  return { month, setMonth, isMonthValid: /^\d{4}-(0[1-9]|1[0-2])$/.test(month), isDownloading, download };
};

export type UsageReportManager = ReturnType<typeof useUsageReport>;
